import { useState } from "react";
import ItemDetailDialog from "@/components/ItemDetailDialog";

export default function InventoryGrid({ items }) {
  const [active, setActive] = useState(null);

  if (!items || items.length === 0) {
    return (
      <div
        className="text-center py-16 text-slate-500"
        data-testid="inventory-empty-state"
      >
        <i className="fas fa-box-open text-5xl opacity-20 mb-4" />
        <p className="font-black uppercase tracking-widest text-sm">
          Inventory loading...
        </p>
      </div>
    );
  }

  return (
    <>
      <div
        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6"
        data-testid="inventory-grid"
      >
        {items.map((item) => (
          <ItemCard key={item.id} item={item} onOpen={() => setActive(item)} />
        ))}
      </div>

      <ItemDetailDialog
        item={active}
        open={!!active}
        onOpenChange={(o) => !o && setActive(null)}
      />
    </>
  );
}

function ItemCard({ item, onOpen }) {
  const accentBorders = ["border-b-brand-gold", "border-b-brand-red", "border-b-brand-blue"];
  const accent = accentBorders[(item.sort_order || 0) % accentBorders.length];
  const goingFast = item.going_fast && !item.sold_out;
  const soldOut = item.sold_out;

  return (
    <button
      type="button"
      onClick={onOpen}
      className={`group text-left bg-white rounded-[32px] overflow-hidden shadow-sm border border-slate-100 hover:shadow-2xl hover:-translate-y-1 transition-all border-b-8 ${accent} focus:outline-none focus:ring-4 focus:ring-brand-blue/20`}
      data-testid={`inventory-card-${item.id}`}
    >
      <div className="h-44 bg-slate-100 relative overflow-hidden">
        {item.image_url ? (
          <img
            src={item.image_url}
            alt={item.name}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-brand-blue">
            <i className={`fas ${item.icon} text-5xl`} />
          </div>
        )}

        {/* Top-left badges (status / scarcity) */}
        <div className="absolute top-3 left-3 flex flex-col gap-1.5 items-start max-w-[80%]">
          {soldOut ? (
            <span className="bg-slate-900 text-white text-[9px] font-black uppercase tracking-widest px-2.5 py-1 rounded-full">
              Fully Booked
            </span>
          ) : goingFast ? (
            <span
              className="bg-brand-red text-white text-[9px] font-black uppercase tracking-widest px-2.5 py-1 rounded-full inline-flex items-center gap-1"
              data-testid={`inventory-card-${item.id}-going-fast`}
            >
              <i className="fas fa-fire text-[8px]" /> Going Fast
            </span>
          ) : null}

          {item.instant_booking && !soldOut && (
            <span
              className="bg-brand-gold text-brand-blue text-[9px] font-black uppercase tracking-widest px-2.5 py-1 rounded-full inline-flex items-center gap-1"
              data-testid={`inventory-card-${item.id}-instant`}
            >
              <i className="fas fa-bolt text-[8px]" /> Instant Booking
            </span>
          )}
        </div>
      </div>

      <div className="p-5">
        <h4 className="font-black text-base text-brand-blue uppercase tracking-tightest leading-tight">
          {item.name}
        </h4>
        <p className="text-[11px] text-slate-500 font-medium mt-1.5 line-clamp-2 min-h-[32px]">
          {item.description}
        </p>

        <div className="flex items-baseline justify-between mt-3">
          <p className="text-2xl font-black text-brand-red">
            ${item.price_per_day.toFixed(2)}
            <span className="text-[10px] text-slate-400 font-bold ml-1">/day</span>
          </p>
          {item.extras && item.extras.length > 0 && (
            <span className="text-[10px] font-black uppercase tracking-widest text-slate-400">
              + add-ons
            </span>
          )}
        </div>

        <div className="mt-4 inline-flex items-center gap-2 text-brand-blue font-black uppercase text-[10px] tracking-widest group-hover:text-brand-red transition-colors">
          <i className="fas fa-arrow-right group-hover:translate-x-1 transition-transform" />
          {soldOut ? "View Details" : "Customize & Reserve"}
        </div>
      </div>
    </button>
  );
}
